import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useCurrentUser, useAuthToken, useAuthState, useSignOut } from '@mystudioclass/auth';
import { setTokenGetter } from '../services/api';
import toast from 'react-hot-toast';

// API_URL should include /api suffix (same as api.js)
const API_URL = import.meta.env.VITE_API_URL || 'https://studioscale-backend-production.up.railway.app/api';

const CustomerAuthContext = createContext(null);

export const CustomerAuthProvider = ({ children }) => {
  const { user: betterAuthUser, isLoaded: betterAuthLoaded } = useCurrentUser();
  const { getToken } = useAuthToken();
  const { isSignedIn } = useAuthState();
  const { signOut: betterAuthSignOut } = useSignOut();

  // Initialize from cache immediately to prevent login flash on reload
  const [customer, setCustomer] = useState(() => {
    try {
      const cached = localStorage.getItem('customerData');
      if (cached) {
        const { user } = JSON.parse(cached);
        return user || null;
      }
    } catch (e) { /* ignore */ }
    return null;
  });
  const [studio, setStudio] = useState(() => {
    try {
      const cached = localStorage.getItem('customerData');
      if (cached) {
        const { studio: studioData } = JSON.parse(cached);
        return studioData || null;
      }
    } catch (e) { /* ignore */ }
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [needsRegistration, setNeedsRegistration] = useState(false);

  // Fetch customer data from backend after Better Auth auth with retry
  const fetchCustomerData = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 2;

    // Only clear state if Better Auth has loaded AND user is definitely not signed in
    // This prevents clearing cached data while Better Auth is still determining auth state
    if (betterAuthLoaded && isSignedIn === false) {
      setCustomer(null);
      setStudio(null);
      setLoading(false);
      setNeedsRegistration(false);
      localStorage.removeItem('customerData');
      return;
    }

    // If Better Auth hasn't determined sign-in state yet, wait
    if (!isSignedIn) {
      return;
    }

    // If signed in but user data not loaded yet, wait (don't set loading=false)
    if (!betterAuthUser) {
      return;
    }

    // Set loading true before API call
    setLoading(true);

    try {
      const token = await getToken();

      // If token is null/undefined, wait and retry
      if (!token && retryCount < MAX_RETRIES) {
        console.log('Token not ready, retrying in 500ms...');
        setTimeout(() => fetchCustomerData(retryCount + 1), 500);
        return;
      }

      const response = await fetch(`${API_URL}/profile/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.exists) {
          const { user, studio: studioData } = data.data;

          // Check if user is a student/customer
          if (user?.role === 'student') {
            setCustomer(user);
            setStudio(studioData);
            setNeedsRegistration(false);
            // Cache successful data
            try {
              localStorage.setItem('customerData', JSON.stringify({ user, studio: studioData }));
            } catch (e) { /* ignore storage errors */ }
          } else {
            // User exists but is not a customer (might be studio owner trying to use customer portal)
            setCustomer(null);
            setStudio(null);
            setNeedsRegistration(false);
          }
        } else {
          // User doesn't exist in our database - needs to complete registration
          setCustomer(null);
          setStudio(null);
          setNeedsRegistration(true);
          localStorage.removeItem('customerData');
        }
      } else if (response.status === 404) {
        // User not found in our database - needs to register
        setCustomer(null);
        setStudio(null);
        setNeedsRegistration(true);
        localStorage.removeItem('customerData');
      } else {
        console.error('Failed to fetch customer data:', response.status);
        // Server error - try to use cached data, don't redirect to registration
        const cached = localStorage.getItem('customerData');
        if (cached) {
          try {
            const { user, studio: studioData } = JSON.parse(cached);
            setCustomer(user);
            setStudio(studioData);
            setNeedsRegistration(false);
          } catch (e) {
            setCustomer(null);
            setStudio(null);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);

      // Network error - retry if we haven't exceeded max retries
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying fetch (attempt ${retryCount + 2}/${MAX_RETRIES + 1})...`);
        setTimeout(() => fetchCustomerData(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }

      // After retries fail, try to use cached data instead of redirecting to registration
      const cached = localStorage.getItem('customerData');
      if (cached) {
        try {
          const { user, studio: studioData } = JSON.parse(cached);
          console.log('Using cached customer data after network error');
          setCustomer(user);
          setStudio(studioData);
          setNeedsRegistration(false);
        } catch (e) {
          setCustomer(null);
          setStudio(null);
          // Only set needsRegistration if we have NO cached data
          // This prevents redirect on network failures for existing users
        }
      } else {
        // No cached data - but DON'T assume they need registration
        // Just leave them in a loading state or show error
        setCustomer(null);
        setStudio(null);
        // DON'T set needsRegistration(true) on network errors!
      }
    } finally {
      setLoading(false);
    }
  }, [betterAuthLoaded, isSignedIn, betterAuthUser, getToken]);

  // Fetch customer data when Better Auth auth state changes
  useEffect(() => {
    if (betterAuthLoaded) {
      fetchCustomerData();
    }
  }, [betterAuthLoaded, isSignedIn, betterAuthUser?.id, fetchCustomerData]);

  // Set token getter for api.js to use for authenticated requests
  useEffect(() => {
    if (isSignedIn && getToken) {
      setTokenGetter(() => getToken());
    } else {
      setTokenGetter(null);
    }
  }, [isSignedIn, getToken]);

  const logout = async () => {
    try {
      await betterAuthSignOut();
      setCustomer(null);
      setStudio(null);
      setNeedsRegistration(false);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to log out');
    }
  };

  // Refresh customer data (call after completing registration)
  const refreshCustomerData = () => {
    setLoading(true);
    fetchCustomerData();
  };

  const value = {
    // Better Auth user data
    betterAuthUser,
    isSignedIn,

    // Customer-specific data
    customer,
    user: customer, // Alias for compatibility
    studio,

    // State
    loading: !betterAuthLoaded || loading,
    // Trust cached customer data while auth is still loading
    // Only require isSignedIn once auth has fully loaded
    isAuthenticated: betterAuthLoaded ? (isSignedIn && !!customer) : !!customer,
    needsRegistration,

    // Actions
    logout,
    refreshCustomerData,
    setCustomer,
    setUser: setCustomer, // Alias for compatibility
    setStudio,
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
};

// Alias for easier migration
export const useAuth = useCustomerAuth;
